/**
 * Клиентские отладочные записи: всегда в консоль браузера, при явном включении —
 * ещё и на сервер в /api/debug-log, где они ложатся в файл.
 *
 * Пересылка по умолчанию выключена и требует двух условий сразу: сборка с
 * NEXT_PUBLIC_DEBUG_LOG=1 (стенд для разбора, не боевой) и `?debug=1` у самого
 * проверяющего. Записи копятся в очередь и уходят пачкой раз в пять секунд —
 * прежняя версия слала запрос на каждую строку и на странице прокторинга давала
 * десятки запросов в минуту поверх самой комнаты.
 */

const RELAY_BUILD_ENABLED = process.env.NEXT_PUBLIC_DEBUG_LOG === "1";

const FLUSH_MS = 5_000;
const MAX_QUEUE = 50;

type QueueEntry = { level: string; message: string; t: number };

let queue: QueueEntry[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;

function safeStringify(v: unknown): string {
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

const method = (level: string): "warn" | "error" | "info" | "log" => {
  if (level === "warn") return "warn";
  if (level === "error") return "error";
  if (level === "info") return "info";
  return "log";
};

function relayOn(): boolean {
  if (!RELAY_BUILD_ENABLED) return false;
  if (typeof window === "undefined") return false;

  try {
    if (window.location.search.includes("debug=1")) return true;
    return window.localStorage.getItem("debug") === "1";
  } catch {
    return false;
  }
}

function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  if (!queue.length) return;

  const entries = queue;
  queue = [];

  void fetch("/api/debug-log", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entries }),
    keepalive: true,
  }).catch(() => {});
}

function bindListeners(): void {
  if (listenersBound) return;
  listenersBound = true;

  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}

function enqueue(level: string, message: string): void {
  bindListeners();
  queue.push({ level, message, t: Date.now() });

  if (queue.length >= MAX_QUEUE) {
    flush();
    return;
  }

  if (!timer) timer = setTimeout(flush, FLUSH_MS);
}

export function dlog(message: unknown, level: string = "log") {
  if (typeof window === "undefined") return;

  const text = safeStringify(message);
  console[method(level)]("[dbg]", text);

  if (relayOn()) enqueue(level, text);
}

/**
 * Оставлено ради вызовов, которые ждут этой функции: перехватывать console
 * больше незачем — записи и так видны в консоли.
 */
export function installConsoleRelay() {
  return;
}
